/**
 * File: components/auth/InviteAccept.jsx
 * =======================================
 * Handles invitation acceptance flow
 *
 * Flow:
 * 1. Validate invite token
 * 2. If user not logged in → redirect to login with invite token
 * 3. If logged in → show accept button
 * 4. On accept → add access and redirect to dashboard
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext.jsx';

const ROLE_LABELS = {
  co_owner: 'Co-Owner',
  family_member: 'Family Member',
  service_provider: 'Service Provider'
};

export default function InviteAccept() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, user, getToken } = useAuth();

  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [error, setError] = useState('');
  const [invite, setInvite] = useState(null);
  const [success, setSuccess] = useState(false);

  // Validate invite token on mount
  useEffect(() => {
    validateInvite();
  }, [token]);

  const validateInvite = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`/api/invites/validate/${token}`);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Invalid invitation');
      }

      setInvite(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async () => {
    if (!isAuthenticated) {
      // Store token and redirect to login
      sessionStorage.setItem('pendingInviteToken', token);
      navigate('/login');
      return;
    }

    setAccepting(true);
    setError('');

    try {
      const authToken = getToken();
      const response = await fetch(`/api/invites/${token}/accept`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to accept invitation');
      }

      setSuccess(true);

      // Redirect based on role
      setTimeout(() => {
        if (data.role === 'service_provider') {
          navigate('/my-tasks');
        } else {
          navigate('/');
        }
      }, 2000);
    } catch (err) {
      setError(err.message);
    } finally {
      setAccepting(false);
    }
  };

  // Check for pending invite after login
  useEffect(() => {
    if (isAuthenticated) {
      const pendingToken = sessionStorage.getItem('pendingInviteToken');
      if (pendingToken && pendingToken === token && invite) {
        // Auto-accept after login
        sessionStorage.removeItem('pendingInviteToken');
        handleAccept();
      }
    }
  }, [isAuthenticated, invite]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-500">Validating invitation...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg max-w-md w-full p-8 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Invalid Invitation</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/login')}
            className="px-6 py-2 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg max-w-md w-full p-8 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Welcome to HarbourNest!</h2>
          <p className="text-gray-600">You now have access. Redirecting...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-lg max-w-md w-full p-8">
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900">You've Been Invited!</h2>
        </div>

        <div className="space-y-4 mb-6">
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="text-sm text-gray-500">Invited by</div>
            <div className="font-medium text-gray-900">{invite.inviterName}</div>
          </div>

          <div className="bg-gray-50 rounded-lg p-4">
            <div className="text-sm text-gray-500">Your role</div>
            <div className="font-medium text-gray-900">
              {ROLE_LABELS[invite.role] || invite.role}
            </div>
          </div>

          <div className="bg-gray-50 rounded-lg p-4">
            <div className="text-sm text-gray-500">Access to</div>
            <div className="font-medium text-gray-900">
              {invite.scope === 'portfolio' ? 'All properties' : 'Specific property'}
            </div>
          </div>
        </div>

        {!isAuthenticated && (
          <p className="text-sm text-gray-500 text-center mb-4">
            You'll need to sign in or create an account to accept this invitation.
          </p>
        )}

        {isAuthenticated && invite.email !== user?.email?.toLowerCase() && (
          <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 text-yellow-800 rounded-lg text-sm">
            This invitation was sent to <strong>{invite.email}</strong> but you're signed in as <strong>{user.email}</strong>.
          </div>
        )}

        <button
          onClick={handleAccept}
          disabled={accepting}
          className={`w-full py-3 px-4 rounded-lg font-medium text-white ${
            accepting
              ? 'bg-gray-400 cursor-not-allowed'
              : 'bg-indigo-600 hover:bg-indigo-700'
          }`}
        >
          {accepting
            ? 'Accepting...'
            : isAuthenticated
              ? 'Accept Invitation'
              : 'Sign In to Accept'
          }
        </button>
      </div>
    </div>
  );
}
